import { OAuthProvider } from 'firebase/auth';
import { linkWithPopup, reauthenticateWithPopup, unlink } from 'firebase/auth';
import { useFirebaseAuth } from '@/providers/FirebaseProvider';
import { jwtDecode } from 'jwt-decode';
import { useCallback, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/components/layout/Toast';

export const useCarbonHubSSO = (maxRetries = 5, retryDelayMs = 10000) => {
  const toast = useToast();
  const { t } = useTranslation();
  const firebaseAuth = useFirebaseAuth();
  const firebaseUser = firebaseAuth.currentUser;
  const retryInterval = useRef<NodeJS.Timeout | null>(null);
  const retryCount = useRef(0);

  const carbonHubProviderId = process.env.INTEGRATIONS_SAMI_OIDC;
  const samiOAuthProvider = carbonHubProviderId
    ? new OAuthProvider(carbonHubProviderId)
    : null;

  const getStorageKey = () => {
    if (!firebaseUser) return null;
    return `${firebaseUser.uid}_carbon_hub_access_token`;
  };

  const setAccessToken = (token: string) => {
    const storageKey = getStorageKey();
    if (!storageKey) return;
    localStorage.setItem(storageKey, token);
  };

  const getAccessToken = () => {
    const storageKey = getStorageKey();
    if (!storageKey) return null;
    return localStorage.getItem(storageKey);
  };

  const isAccessTokenValid = useCallback(() => {
    const accessToken = getAccessToken();
    if (!accessToken) return false;

    const decodedToken = jwtDecode<{ exp?: number }>(accessToken);
    const currentTimestamp = Date.now() / 1000;
    return decodedToken.exp ? currentTimestamp < decodedToken.exp : false;
  }, [firebaseUser]);

  const isLinkedToCarbonHub = useCallback(() => {
    return (
      !!carbonHubProviderId &&
      (firebaseUser?.providerData.some(
        (provider) => provider?.providerId === carbonHubProviderId,
      ) ??
        false)
    );
  }, [firebaseUser, carbonHubProviderId]);

  const isLinkingInProgress = useRef(false);

  const linkToCarbonAccount = useCallback(() => {
    if (!firebaseUser || !samiOAuthProvider || isLinkingInProgress.current) {
      return;
    }

    isLinkingInProgress.current = true;
    const openPopup = isLinkedToCarbonHub()
      ? reauthenticateWithPopup(firebaseUser, samiOAuthProvider)
      : linkWithPopup(firebaseUser, samiOAuthProvider);

    openPopup
      .then((userCredential) => {
        toast.openToastWithMessage(t('toast:user.link_to_carbon_hub.success'));
        const cred = OAuthProvider.credentialFromResult(userCredential);
        if (cred?.accessToken) setAccessToken(cred.accessToken);
      })
      .catch((error) => {
        toast.openToastWithError(t('toast:user.link_to_carbon_hub.error'));
        console.error(error);
      })
      .finally(() => {
        isLinkingInProgress.current = false;
      });
  }, [firebaseUser, samiOAuthProvider, toast, t, isLinkedToCarbonHub]);

  const unlinkFromCarbonHub = useCallback(() => {
    if (!firebaseUser || !samiOAuthProvider) return;

    unlink(firebaseUser, samiOAuthProvider.providerId)
      .then(() => {
        toast.openToastWithMessage(
          t('toast:user.unlink_from_carbon_hub.success'),
        );
        setAccessToken('');
      })
      .catch((error) => {
        toast.openToastWithError(t('toast:user.unlink_from_carbon_hub.error'));
        console.error(error);
      });
  }, [firebaseUser, samiOAuthProvider, toast, t]);

  const attemptLinking = useCallback(() => {
    if (!isLinkedToCarbonHub() || isAccessTokenValid()) {
      retryCount.current = 0;
      if (retryInterval.current) clearTimeout(retryInterval.current);
      return;
    }

    if (retryCount.current >= maxRetries) {
      toast.openToastWithError(t('toast:user.link_to_carbon_hub.max_retries'));
      return;
    }

    linkToCarbonAccount();
    retryCount.current += 1;

    retryInterval.current = setTimeout(attemptLinking, retryDelayMs);
  }, [
    isLinkedToCarbonHub,
    isAccessTokenValid,
    linkToCarbonAccount,
    maxRetries,
    retryDelayMs,
    toast,
    t,
  ]);

  return {
    linkToCarbonAccount,
    unlinkFromCarbonHub,
    isLinkedToCarbonHub: isLinkedToCarbonHub(),
    getAccessToken,
    isAccessTokenValid: isAccessTokenValid(),
  };
};
